import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { makeStyles } from "@material-ui/core/styles";
import { DateTime } from "luxon";

import { createBookingMutation } from "../../../Booking/mutations";
import { StripeElementsInput } from "../../../../components/StripeElementsInput";
import { useAppConfig } from "../../../../components/AppConfigContext";
import api from "../../../../lib/api";
import { formatPlate } from "../../../../lib/misc";
import { formatCurrency, timings } from "../../../../lib/rateCardUtils";
import { offsiteBookingGuestCardsQuery } from "../queries";
import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import { toDateInput, addDays, nightsBetween } from "../data";
import Overlay from "../../components/Overlay";

/* ---------- Create an on-behalf booking (awaiting payment) ---------- */

export const CALCULATION_PLATE = "DRV-00000000000000";
const AVAILABILITY_DEBOUNCE_MS = 450;

export function makePlaceholderPlate(timezone) {
  return `DRV-${DateTime.now().setZone(timezone || "local").toFormat("ddHHmmss")}`;
}

const useStyles = makeStyles({
  dialog: { width: "100%", maxWidth: 540, borderRadius: 12, border: `1px solid ${T.border}`, background: T.elevated, padding: 24, boxShadow: "0 20px 50px rgba(0,0,0,0.25)" },
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 },
  heading: { fontSize: 22, fontWeight: 700, color: T.fg },
  sub: { marginTop: 4, fontSize: 12, color: T.mutedFg },
  closeBtn: { border: "none", background: "transparent", fontSize: 14, color: T.mutedFg, cursor: "pointer", fontFamily: "inherit" },
  stack: { marginTop: 16, display: "flex", flexDirection: "column", gap: 16 },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  label: { display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: T.subtleFg },
  field: { marginTop: 4 },
  hint: { marginTop: 4, display: "block", fontSize: 10, color: T.subtleFg },
  lockCard: { borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, padding: "10px 12px" },
  lockName: { marginTop: 2, fontWeight: 600, color: T.fg },
  lockMeta: { marginTop: 2, fontSize: 12, color: T.mutedFg },
  estimate: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", borderRadius: 6, background: T.surface, padding: "8px 12px", fontSize: 12, color: T.mutedFg },
  feeRows: { display: "flex", flexDirection: "column", gap: 6, width: "100%" },
  feeRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 },
  feeLabel: { color: T.mutedFg },
  feeValue: { fontWeight: 600, color: T.fg },
  feeNote: { fontSize: 11, color: T.mutedFg },
  availability: { borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, padding: "8px 12px", fontSize: 12, color: T.mutedFg },
  availabilityError: { color: T.warningFg },
  stripeBox: {
    marginTop: 6,
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: T.surface,
    padding: "10px 12px",
    fontSize: 12,
    color: T.mutedFg,
    "& button": {
      fontFamily: "inherit",
      fontSize: "13px !important",
      height: "32px !important",
      minHeight: "32px !important",
      paddingLeft: "14px !important",
      paddingRight: "14px !important",
      borderRadius: "6px !important",
      marginTop: "0 !important",
      marginBottom: "0 !important",
    },
    "& a": {
      fontSize: 12,
    },
  },
  footer: { marginTop: 8, display: "flex", justifyContent: "flex-end", gap: 8 },
  cancelBtn: { border: "none", background: "transparent" },
});

function toBookingDate(value, rateCard, edge, timezone) {
  if (!value || !rateCard) return undefined;

  const minutes = edge === "start" ? rateCard.entryAfterTime : rateCard.exitBeforeTime;

  return DateTime
    .fromISO(value, { zone: timezone })
    .plus({ minutes: minutes || 0 })
    .toISO();
}

function rateCardLabel(card) {
  if (!card) return "";

  return card.version ? `${card.name} (version ${card.version})` : card.name;
}

export function dayOverlapsFullRange(day, range) {
  if (!range?.full) return false;

  const start = range.startDate;
  const end = typeof range.endDate === "string" ? range.endDate.slice(0, 10) : range.endDate;

  return day >= start && day <= end;
}

export function hasFullDate({ dates, checkIn, checkOut }) {
  const start = DateTime.fromISO(checkIn);
  const end = DateTime.fromISO(checkOut);

  if (!start.isValid || !end.isValid) return true;

  let day = start;

  while (day <= end) {
    const value = day.toISODate();

    if ((dates || []).some((range) => dayOverlapsFullRange(value, range))) {
      return true;
    }

    day = day.plus({ days: 1 });
  }

  return false;
}

export function isDailyLotGuestCard(card) {
  return card?.type === "DAILY" && Boolean(card?.lot?.id);
}

export function isSupportedOffsiteGuestCard(card) {
  return isDailyLotGuestCard(card) &&
    !card.guestCompany &&
    !card.guestDepartment &&
    !card.guestRoom &&
    !card.guestApartmentNumber &&
    !card.guestReservation &&
    !card.guestEmployeeCode &&
    !card.guestPosition &&
    !card.includeCarType &&
    !card.includeCarNotes;
}

export default function CreateBookingDialog({ installation, facility, lots, onCancel, onCreate }) {
  const s = useStyles();
  const ui = useUi();
  const appConfig = useAppConfig();
  const today = toDateInput(new Date(), installation?.timeZoneName);
  const tomorrow = toDateInput(addDays(new Date(), 1, installation?.timeZoneName), installation?.timeZoneName);
  const [guestName, setGuestName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [plate, setPlate] = useState("");
  const [checkIn, setCheckIn] = useState(today);
  const [checkOut, setCheckOut] = useState(tomorrow);
  const [guestCardId, setGuestCardId] = useState("");
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [availability, setAvailability] = useState({ loading: false, available: false, message: "Select a guest card and dates.", details: null });
  const [submitError, setSubmitError] = useState("");

  const { data: guestCardsData, loading: guestCardsLoading, error: guestCardsError } = useQuery(
    offsiteBookingGuestCardsQuery,
    {
      variables: { installationId: installation?.id },
      fetchPolicy: "no-cache",
      skip: !installation?.id,
    },
  );
  const [createBooking, { loading: creating }] = useMutation(createBookingMutation);

  const guestCards = useMemo(
    () => (guestCardsData?.allRateCards || []).filter(isSupportedOffsiteGuestCard),
    [guestCardsData],
  );
  const selectedGuestCard = guestCards.find((card) => card.id === guestCardId);
  const selectedLot = lots.find((lot) => lot.id === selectedGuestCard?.lot?.id) || selectedGuestCard?.lot;
  const isSingleDayDaily = selectedGuestCard?.type === "DAILY" && selectedGuestCard?.calculationMode === "SINGLE_DAY";
  const effectiveCheckOut = isSingleDayDaily ? checkIn : checkOut;
  const startAt = toBookingDate(checkIn, selectedGuestCard, "start", installation?.timeZoneName);
  const endAt = toBookingDate(effectiveCheckOut, selectedGuestCard, "end", installation?.timeZoneName);
  const days = nightsBetween(checkIn, effectiveCheckOut);
  const scheduledTotal = availability.details?.billing?.scheduled?.fee?.total;
  const scheduledTotalLabel = scheduledTotal == null
    ? null
    : formatCurrency(Number(scheduledTotal), installation?.currency);
  const contactOk = email.trim().length > 3 || mobile.trim().length > 5;
  const needsStripe = selectedGuestCard?.paymentStripe === true;
  /* Not `!needsStripe`: with no card picked yet there is nothing to say about
     how it bills, and the estimate stays on its single-figure line. */
  const hotelInvoiced = Boolean(selectedGuestCard) && !needsStripe;
  const zeroLabel = formatCurrency(0, installation?.currency);
  const canPayLater = needsStripe && selectedGuestCard?.valetPayLater === true;
  const canSubmit =
    !creating &&
    !availability.loading &&
    availability.available &&
    selectedGuestCard &&
    guestName.trim().length > 1 &&
    contactOk &&
    (!needsStripe || canPayLater || stripeCustomerId) &&
    new Date(effectiveCheckOut).getTime() >= new Date(checkIn).getTime();

  useEffect(() => {
    if (!guestCardId && guestCards.length) {
      setGuestCardId(guestCards[0].id);
    }
  }, [guestCardId, guestCards]);

  useEffect(() => {
    setStripeCustomerId(null);
  }, [guestCardId]);

  useEffect(() => {
    if (isSingleDayDaily && checkOut !== checkIn) {
      setCheckOut(checkIn);
    }
  }, [checkIn, checkOut, isSingleDayDaily]);

  useEffect(() => {
    let cancelled = false;
    let debounceTimer = null;

    async function checkAvailability() {
      if (!installation?.id || !selectedGuestCard || !startAt || !endAt) {
        setAvailability({ loading: false, available: false, message: "Select a guest card and dates.", details: null });
        return;
      }

      setAvailability({ loading: true, available: false, message: "Checking lot availability...", details: null });

      try {
        const { data: lotStatus } = await api.axiosService.post(
          "/public/lot/full_dates",
          {
            installationId: installation.id,
            rateCardId: selectedGuestCard.id,
          },
        );
        const lotIsFull = lotStatus?.result || hasFullDate({
          dates: lotStatus?.dates,
          checkIn,
          checkOut: effectiveCheckOut,
        });

        if (lotIsFull) {
          if (!cancelled) {
            setAvailability({
              loading: false,
              available: false,
              message: "Booking is not available for these dates.",
              details: null,
            });
          }

          return;
        }

        if (!cancelled) {
          setAvailability({ loading: true, available: false, message: "Calculating booking details...", details: null });
        }

        const details = await timings(
          {
            startDay: startAt,
            endDay: endAt,
            plate: CALCULATION_PLATE,
          },
          installation.id,
          selectedGuestCard.id,
        );

        if (!cancelled) {
          setAvailability({ loading: false, available: true, message: "", details });
        }
      } catch (err) {
        if (!cancelled) {
          setAvailability({
            loading: false,
            available: false,
            message: err.message || "Booking is not available for these dates.",
            details: null,
          });
        }
      }
    }

    debounceTimer = setTimeout(checkAvailability, AVAILABILITY_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
    };
  }, [checkIn, effectiveCheckOut, endAt, installation?.id, selectedGuestCard, startAt]);

  const submit = async () => {
    setSubmitError("");
    const cleanPlate = plate.trim()
      ? formatPlate(plate)
      : makePlaceholderPlate(installation?.timeZoneName);

    try {
      const { data } = await createBooking({
        variables: {
          installationId: installation.id,
          rateCardId: selectedGuestCard.id,
          plate: cleanPlate,
          startDay: startAt,
          endDay: endAt,
          guestName: guestName.trim(),
          guestEmail: email.trim(),
          guestPhone: mobile.trim(),
          guestCompany: "",
          guestDepartment: "",
          guestRoom: "",
          guestApartmentNumber: "",
          guestReservation: "",
          guestEmployeeCode: "",
          guestPosition: "",
          stripeCustomerId,
          payLater: canPayLater && !stripeCustomerId,
          carType: "",
          carNotes: "",
        },
        refetchQueries: ["offsiteTicketLotDashboard"],
        awaitRefetchQueries: true,
      });

      onCreate(data?.createBooking);
    } catch (err) {
      setSubmitError(err.message || "Booking could not be created. Please check the details and try again.");
    }
  };

  return (
    <Overlay onClose={onCancel}>
      <div className={s.dialog}>
        <div className={s.head}>
          <div>
            <div className={s.heading}>Create booking</div>
            <div className={s.sub}>
              {hotelInvoiced
                ? "Creates a booking the hotel invoices. A ticket can be allocated straight away."
                : "Creates an unpaid booking. Take payment before allocating a ticket."}
            </div>
          </div>
          <button onClick={onCancel} className={s.closeBtn}>x</button>
        </div>

        <div className={s.stack}>
          {submitError && <div className={s.availabilityError}>{submitError}</div>}

          <div className={s.grid2}>
            <div>
              <label className={s.label}>Guest name *</label>
              <input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="e.g. Nick" className={cx(ui.field, s.field)} />
            </div>
            <div>
              <label className={s.label}>Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="guest@drivo.com.au" className={cx(ui.field, s.field)} />
            </div>
          </div>

          <div>
            <label className={s.label}>Mobile number</label>
            <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+61 4xx xxx xxx" className={cx(ui.field, s.field)} />
            <span className={s.hint}>Email or mobile is required.</span>
          </div>

          <div>
            <label className={s.label}>Plate</label>
            <input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="BXR 44K" className={cx(ui.field, s.field)} />
          </div>

          <div>
            <label className={s.label}>Guest Card</label>
            <select
              value={guestCardId}
              onChange={(e) => setGuestCardId(e.target.value)}
              className={cx(ui.field, s.field)}
              disabled={guestCardsLoading}
            >
              <option value="">{guestCardsLoading ? "Loading guest cards..." : "Select a guest card"}</option>
              {guestCards.map((card) => (
                <option key={card.id} value={card.id}>{rateCardLabel(card)}</option>
              ))}
            </select>
            {guestCardsError && <span className={s.hint}>Guest cards could not be loaded.</span>}
          </div>

          {selectedGuestCard && (
            <div className={s.lockCard}>
              <label className={s.label}>Lot</label>
              <div className={s.lockName}>{selectedLot?.name || selectedGuestCard.lot?.name || "No lot assigned"}</div>
              <div className={s.lockMeta}>
                {selectedLot?.type === "OFFSITE" ? "Off-site" : selectedLot?.type === "ONSITE" ? "On-site" : "Assigned to selected guest card"}
                {selectedLot?.walkingDistanceMinutes != null ? ` - ${selectedLot.walkingDistanceMinutes} min walk` : ""}
              </div>
            </div>
          )}

          {isSingleDayDaily ? (
            <div>
              <label className={s.label}>Date</label>
              <input
                type="date"
                value={checkIn}
                onChange={(e) => {
                  const value = e.target.value;
                  setCheckIn(value);
                  setCheckOut(value);
                }}
                className={cx(ui.field, s.field)}
              />
            </div>
          ) : (
            <div className={s.grid2}>
              <div>
                <label className={s.label}>Check-in</label>
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCheckIn(value);
                    if (new Date(checkOut).getTime() < new Date(value).getTime()) {
                      setCheckOut(value);
                    }
                  }}
                  className={cx(ui.field, s.field)}
                />
              </div>
              <div>
                <label className={s.label}>Check-out</label>
                <input type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)} className={cx(ui.field, s.field)} />
              </div>
            </div>
          )}

          <div className={s.estimate}>
            <span>{days} day{days === 1 ? "" : "s"}</span>
            {!availability.available || !scheduledTotalLabel ? (
              <span className={s.availabilityError}>{availability.message}</span>
            ) : hotelInvoiced ? (
              /* Nothing is collected here, so the desk gets both figures: what
                 it must take now, and what the hotel will invoice. */
              <div className={s.feeRows}>
                <div className={s.feeRow}>
                  <span className={s.feeLabel}>Amount due</span>
                  <span className={s.feeValue}>{zeroLabel}</span>
                </div>
                <div className={s.feeRow}>
                  <span className={s.feeLabel}>Total Fees</span>
                  <span className={s.feeValue}>{scheduledTotalLabel}</span>
                </div>
                <span className={s.feeNote}>Invoiced by the hotel, not taken at the desk.</span>
              </div>
            ) : (
              <span>Total: {scheduledTotalLabel}</span>
            )}
          </div>

          {needsStripe && (
            <div>
              <label className={s.label}>Payment method{canPayLater ? "" : " *"}</label>
              <div className={s.availability}>
                {canPayLater
                  ? "Payment method is optional for this guest card. If no card is added now, the booking will be created unpaid."
                  : "This guest card requires a payment method before the booking can be created."}
              </div>
              <div className={s.stripeBox}>
                <StripeElementsInput
                  appConfig={appConfig}
                  buttonSize="small"
                  currency={installation.currency}
                  country={facility?.city?.country}
                  installationId={installation.id}
                  guestEmail={email.trim()}
                  stripeCustomerId={stripeCustomerId}
                  setStripeCustomerId={setStripeCustomerId}
                />
              </div>
            </div>
          )}

          <div className={s.footer}>
            <button onClick={onCancel} className={cx(ui.mutedBtn, s.cancelBtn)}>Cancel</button>
            <button disabled={!canSubmit} onClick={submit} className={ui.primaryBtn}>Create booking</button>
          </div>
        </div>
      </div>
    </Overlay>
  );
}
